import { saveToSessionStorage, ssKeys } from '~features/storage';
import { serializeQueryArgs } from '~services/utils';
import { api, apiTags } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';
import indexDb from '~utils/indexDb';

import type {
	ActivateGeneratedBotApiResponse,
	BotWizardApiGenerateBotPrompt,
	BotWizardApiResponse,
	GeneratedMedia,
	GenerateImagesApiRequest,
	GenerateImagesApiResponse,
	GenerateMediaApiResponse,
	GenerateVideoFromImageApiResponse,
	GenerateVideosApiRequest,
	GenerateVideosApiResponse,
	GetGenerationBotQuotaApiResponse,
	GetGenerationImagesQuotaApiResponse,
	GetGenerationImagesSettingsApiResponse,
	GetGenerationMediaBotsApiResponse,
	GetGenerationVideosQuotaApiResponse,
	GetGenerationVideosSettingsApiResponse,
	MediaReaction,
} from './types';

export function prepareMediaItem(item: GeneratedMedia, isPublic = false) {
	const { imageLinks, mediaLinks, type, ...rest } = item as GeneratedMedia & {
		imageLinks?: unknown;
	};

	return {
		type: type || (imageLinks ? 'image' : 'video'),
		mediaLinks: imageLinks || mediaLinks,
		isPublic,
		...rest,
	} as GeneratedMedia;
}

export function prepareMedia(media: GeneratedMedia[], isPublic = false) {
	return media.map((it) => prepareMediaItem(it, isPublic));
}

export const {
	util,
	// character
	useGetBotWizardSettingsQuery,
	useGeneratePromptMutation,
	useGetGenerationBotQuotaQuery,
	useActivateGeneratedBotMutation,
	useGenerateBotPromptMutation,
	useGenerateBotPresetMutation,
	// media
	useGetGeneratedMediaQuery,
	useGetGeneratedPublicMediaQuery,
	useImprovePromptMutation,
	useDeleteGeneratedMediaMutation,
	useAddMediaReactionMutation,
	useDeleteMediaReactionMutation,
	useReportAndDeleteMediaMutation,
	// images
	useGetGenerationImagesSettingsQuery,
	useGetGenerationImagesBotsQuery,
	useGetGenerationImagesQuotaQuery,
	useGenerateImagesMutation,
	// videos
	useGetGenerationVideosSettingsQuery,
	useGetGenerationVideosBotsQuery,
	useGetGenerationVideosQuotaQuery,
	useGenerateVideosMutation,
	useGenerateVideoFromImageMutation,
	useGenerateVideoFromMessageImageMutation,
	// query state
	endpoints: {
		getGeneratedMedia: { useQueryState: useGetGeneratedMediaQueryState },
	},
} = api.injectEndpoints({
	endpoints: (build) => ({
		// character
		getBotWizardSettings: build.query<BotWizardApiResponse, void>({
			// @ts-ignore
			queryFn: async (args, queryApi, extraOptions, baseQuery) => {
				const key = `${serializeQueryArgs('getBotWizardSettingsV2', {})}`;
				const cachedData = (await indexDb.getCache(
					key
				)) as BotWizardApiResponse;
				if (cachedData) {
					queryApi.dispatch(
						util.upsertQueryData('getBotWizardSettings', undefined, cachedData)
					);
				}

				try {
					const response = await baseQuery({
						url: `/ai/v2/generation/bot/settings`,
					});

					if (response.data) {
						void indexDb.putCache(key, response.data);
					}

					return response;
				} catch (error) {
					return { error };
				}
			},
		}),
		generatePrompt: build.mutation<
			{ appearance: string; description: string },
			{ name: string; gender: string; userId: string }
		>({
			query: ({ userId, name, gender }) => ({
				url: `/ai/v2/generation/bot/${userId}/prompt`,
				method: 'POST',
				body: {
					name,
					gender,
				},
			}),
		}),
		generateBotPrompt: build.mutation<
			BotWizardApiGenerateBotPrompt,
			{
				userId: string;
				name: string;
				style: string;
				gender: string;
				appearance: string;
				description: string;
			}
		>({
			query: ({ userId, name, gender, style, appearance, description }) => ({
				url: `/ai/v2/generation/${userId}/prompt/bot`,
				method: 'PUT',
				body: {
					data: {
						name,
						gender,
						style,
						appearance,
						description,
					},
				},
			}),
		}),

		generateBotPreset: build.mutation<
			BotWizardApiGenerateBotPrompt,
			{
				userId: string;
				name: string;
				style: string;
				gender: string;
				age: number;
				height: string;
				physique: string;
				boobs?: string;
				butt?: string;
				penisSize?: string;
				hairStyle: string;
				ethnic: string;
				personality: { [key: string]: number };
				hairColor: string;
				eyeColor: string;
				orientation: string;
				chatStyle: string;
				occupation?: string;
				startingRelationship?: string;
				kinks: string[];
			}
		>({
			query: ({
				userId,
				name,
				gender,
				ethnic,
				style,
				chatStyle,
				age,
				boobs,
				butt,
				hairStyle,
				hairColor,
				eyeColor,
				height,
				kinks,
				occupation,
				orientation,
				penisSize,
				physique,
				personality,
				startingRelationship,
			}) => ({
				url: `/ai/v2/generation/${userId}/guided/bot`,
				method: 'PUT',
				body: {
					data: {
						name,
						gender,
						style,
						ethnic,
						chatStyle,
						age,
						boobs,
						butt,
						hairStyle,
						hairColor,
						eyeColor,
						height,
						kinks,
						occupation,
						orientation,
						penisSize,
						physique,
						startingRelationship,
						personality,
					},
				},
			}),
		}),

		getGenerationBotQuota: build.query<
			GetGenerationBotQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/bot/quota	`,
			}),
			providesTags: [apiTags.characterGenerationQuota],
		}),

		activateGeneratedBot: build.mutation<
			ActivateGeneratedBotApiResponse,
			{ userId: string; botId: number; isPublic: boolean }
		>({
			query: ({ userId, botId, isPublic }) => ({
				url: `ai/generation/${userId}/bot/${botId}/activate`,
				method: 'POST',
				body: {
					access: isPublic ? 'public' : 'private',
				},
			}),
			async onQueryStarted({ isPublic }, { queryFulfilled, dispatch }) {
				try {
					await queryFulfilled;
					if (isPublic) {
						dispatch(api.util.invalidateTags([apiTags.grid]));
					}
				} catch {}
			},
		}),

		// images
		getGenerationImagesSettings: build.query<
			GetGenerationImagesSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/settings`,
			}),
			transformResponse: (
				data: GetGenerationImagesSettingsApiResponse
			): GetGenerationImagesSettingsApiResponse => ({
				...data,
				actions: data.actions || [],
				poses: data.poses || [],
			}),
		}),

		getGenerationImagesBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.imagesGenerationBots],
		}),

		getGenerationImagesQuota: build.query<
			GetGenerationImagesQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/quota`,
			}),
			providesTags: [apiTags.imagesGenerationQuota],
		}),

		generateImages: build.mutation<
			GenerateImagesApiResponse,
			{ userId: string; body: GenerateImagesApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/image`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationImagesQuota',
						{ userId },
						(draft) => {
							const { type } = TokenManager.getInfo();
							if (!body.botId) {
								if (type === 'demo') {
									draft.quotas.free =
										body.imageCount > draft.quotas.demo
											? 0
											: draft.quotas.demo - body.imageCount;
								}
								if (draft.quotas.free) {
									draft.quotas.free =
										body.imageCount > draft.quotas.free
											? 0
											: draft.quotas.free - body.imageCount;
								} else {
									draft.quotas.monthly =
										body.imageCount > draft.quotas.monthly
											? 0
											: draft.quotas.monthly - body.imageCount;
								}
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const images = prepareMedia(
									res.images.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									),
									false
								);

								draft.media.unshift(...images);
								draft.totalLength += images.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: (_, __, args) => {
				const tags = [apiTags.imagesGenerationQuota];
				if (args.body.additionalOptions?.isPublic) {
					tags.push(apiTags.generatedPublicMedia);
				}
				return tags;
			},
		}),

		getGeneratedMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; omit: number; select: number }
		>({
			query: ({ userId, omit, select }) => ({
				url: `/ai/generation/v2/${userId}/media`,
				params: { omit, select },
			}),
			transformResponse: (response: GenerateMediaApiResponse | null) => {
				if (response === null) {
					return {
						receivedCount: 0,
						totalLength: 0,
						media: [],
					};
				}

				const media = prepareMedia(response.media, false);

				return {
					media,
					receivedCount: media.length,
					totalLength: media.length,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.userId}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					// @ts-ignore
					(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
				);
			},
			merge: (currentState, incomingState) => {
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;

				incomingState.media.forEach((item) => {
					const existedIndex = currentState.media.findIndex(
						({ id }) => id === item.id
					);
					if (existedIndex > -1) {
						currentState.media[existedIndex] = item;
						currentState.totalLength -= 1;
					} else {
						currentState.media.push(item);
					}
				});
			},
			providesTags: [apiTags.generatedMedia],
		}),
		getGeneratedPublicMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; omit: number; select: number }
		>({
			query: ({ userId, omit, select }) => ({
				url: `/ai/generation/${userId}/media/public`,
				params: { omit, select },
			}),
			transformResponse: (response: GenerateMediaApiResponse | null) => {
				if (response === null) {
					return {
						receivedCount: 0,
						totalLength: 0,
						media: [],
					};
				}

				const media = prepareMedia(response.media, true);

				return {
					media,
					receivedCount: media.length,
					totalLength: media.length,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.userId}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					// @ts-ignore
					(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
				);
			},
			merge: (currentState, incomingState) => {
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;

				incomingState.media.forEach((item) => {
					const existedIndex = currentState.media.findIndex(
						({ id }) => id === item.id
					);
					if (existedIndex > -1) {
						currentState.media[existedIndex] = item;
						currentState.totalLength -= 1;
					} else {
						currentState.media.push(item);
					}
				});
			},
			providesTags: [apiTags.generatedPublicMedia],
		}),

		deleteGeneratedMedia: build.mutation<
			undefined,
			{ userId: string; media: GeneratedMedia }
		>({
			query: ({ userId, media }) => ({
				url: `ai/generation/${userId}/${media.type}/${media.upstreamId}`,
				method: 'DELETE',
			}),
		}),

		improvePrompt: build.mutation<
			{ prompt: string },
			{ prompt: string; userId: string }
		>({
			query: ({ prompt, userId }) => ({
				url: `ai/generation/${userId}/prompt/improve`,
				method: 'POST',
				body: { prompt },
			}),
		}),

		// videos
		getGenerationVideosSettings: build.query<
			GetGenerationVideosSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/settings`,
			}),
			transformResponse: (
				data: GetGenerationVideosSettingsApiResponse
			): GetGenerationVideosSettingsApiResponse => ({
				...data,
				actions: data.actions || [],
			}),
		}),

		getGenerationVideosQuota: build.query<
			GetGenerationVideosQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/quota`,
			}),
			providesTags: [apiTags.videosGenerationQuota],
		}),

		getGenerationVideosBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.videosGenerationBots],
		}),

		generateVideos: build.mutation<
			GenerateVideosApiResponse,
			{ userId: string; body: GenerateVideosApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/video`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationVideosQuota',
						{ userId },
						(draft) => {
							if (!body.botId) {
								draft.quotas.monthly = Math.max(0, draft.quotas.monthly - 1);
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const videos = prepareMedia(
									res.videos.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									),
									false
								);

								draft.media.unshift(...videos);
								draft.totalLength += videos.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: (_, __, args) => {
				const tags = [apiTags.videosGenerationQuota];
				if (args.body.additionalOptions?.isPublic) {
					tags.push(apiTags.generatedPublicMedia);
				}
				return tags;
			},
		}),

		generateVideoFromImage: build.mutation<
			GenerateVideoFromImageApiResponse,
			{ userId: string; imageGenerationId: string }
		>({
			query: ({ userId, imageGenerationId }) => ({
				url: `/ai/generation/v2/${userId}/video/from/image/${imageGenerationId}`,
				method: 'POST',
			}),
			async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationVideosQuota',
						{ userId },
						(draft) => {
							draft.quotas.monthly = Math.max(0, draft.quotas.monthly - 1);
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const videos = prepareMedia(
									res.videos.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									),
									false
								);

								draft.media.unshift(...videos);
								draft.totalLength += videos.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: [apiTags.videosGenerationQuota],
		}),
		addMediaReaction: build.mutation<
			GenerateImagesApiResponse,
			{
				userId: string;
				id: GeneratedMedia['id'];
				reaction: MediaReaction;
				isPublic?: boolean;
			}
		>({
			query: ({ userId, id, reaction }) => ({
				url: `ai/generation/${userId}/media/${id}/reaction`,
				method: 'PUT',
				body: {
					id,
					reaction,
				},
			}),
			onQueryStarted({ userId, id, reaction }, { dispatch }) {
				const updateCache = (
					endpointName: 'getGeneratedMedia' | 'getGeneratedPublicMedia'
				) => {
					dispatch(
						util.updateQueryData(
							endpointName,
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const media = draft.media.find((it) => it.id === id);
								if (media) {
									media.reactions =
										media.reactions || ({} as Record<MediaReaction, number>);
									media.reactions[reaction] =
										(media.reactions[reaction] || 0) + 1;
									media.userReactions = media.userReactions || [];
									if (!media.userReactions.includes(reaction)) {
										media.userReactions.push(reaction);
									}
								}
							}
						)
					);
				};

				updateCache('getGeneratedMedia');
				updateCache('getGeneratedPublicMedia');
			},
		}),
		deleteMediaReaction: build.mutation<
			GenerateImagesApiResponse,
			{
				userId: string;
				id: GeneratedMedia['id'];
				reaction: MediaReaction;
				isPublic?: boolean;
			}
		>({
			query: ({ userId, id, reaction }) => ({
				url: `ai/generation/${userId}/media/${id}/reaction`,
				method: 'DELETE',
				body: {
					id,
					reaction,
				},
			}),
			onQueryStarted({ userId, id, reaction }, { dispatch }) {
				const updateCache = (
					endpointName: 'getGeneratedMedia' | 'getGeneratedPublicMedia'
				) => {
					dispatch(
						util.updateQueryData(
							endpointName,
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const media = draft.media.find((it) => it.id === id);
								if (media) {
									media.reactions =
										media.reactions || ({} as Record<MediaReaction, number>);
									if (media.reactions[reaction] > 0) {
										media.reactions[reaction] = media.reactions[reaction] - 1;
										if (media.userReactions?.includes(reaction)) {
											media.userReactions = media.userReactions.filter(
												(it) => it !== reaction
											);
										}
									}
								}
							}
						)
					);
				};

				updateCache('getGeneratedMedia');
				updateCache('getGeneratedPublicMedia');
			},
		}),
		reportAndDeleteMedia: build.mutation<
			{},
			{
				id: GeneratedMedia['id'];
				userId: string;
				reason: string;
				reportText: string;
			}
		>({
			query: ({ id, userId, reportText, reason }) => ({
				url: `/ai/generation/${userId}/media/public/report`,
				method: 'POST',
				body: {
					id,
					reason,
					comment: reportText,
				},
			}),
			onQueryStarted({ id, userId }, { dispatch }) {
				const updateCache = (
					endpointName: 'getGeneratedMedia' | 'getGeneratedPublicMedia'
				) => {
					dispatch(
						util.updateQueryData(
							endpointName,
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const mediaIndex = draft.media.findIndex((i) => i.id === id);
								if (mediaIndex !== -1) {
									draft.media.splice(mediaIndex, 1);
									draft.totalLength -= 1;
								}
							}
						)
					);
				};

				updateCache('getGeneratedMedia');
				updateCache('getGeneratedPublicMedia');
			},
		}),

		generateVideoFromMessageImage: build.mutation<
			GenerateVideoFromImageApiResponse,
			{ userId: string; botId: string; messageId: number }
		>({
			query: ({ userId, botId, messageId }) => ({
				url: `/ai/generation/${userId}/video/from/${botId}/message/${messageId}`,
				method: 'POST',
			}),
		}),
	}),
});
